import * as Sentry from '@sentry/react';
import { useEffect, type ReactElement } from 'react';

import { ConnectSlackModal } from '@/components/slack/ConnectSlackModal';
import { DisconnectSlackDialog } from '@/components/slack/DisconnectSlackDialog';
import { getErrorMessage } from '@/hooks/api/errorHelpers';
import {
  useDeleteUserSlackSettings,
  useUpsertUserSlackSettings,
  useUserSlackSettings,
} from '@/hooks/api/useSlackSettings';
import type { slackSettings } from '@kirin/api-client/contracts';

import { SlackIntegrationCard } from './SlackIntegrationCard';
import { useSlackIntegrationController } from './useSlackIntegrationController';

const PERSONAL_SLACK_SCOPE_ID = 'account';

export function PersonalSlackIntegration(): ReactElement {
  const slackQuery = useUserSlackSettings();
  const upsertMutation = useUpsertUserSlackSettings();
  const deleteMutation = useDeleteUserSlackSettings();

  const controller = useSlackIntegrationController({
    saveSettings: (
      data: slackSettings.UpsertSlackSettingsBody
    ): Promise<slackSettings.UpsertSlackSettingsResponse> =>
      upsertMutation.mutateAsync(data),
    deleteSettings: (): Promise<unknown> => deleteMutation.mutateAsync(),
    sentryTags: {
      save: 'slack_save_personal',
      delete: 'slack_delete_personal',
    },
  });

  const slackError = slackQuery.isError
    ? getErrorMessage(slackQuery.error)
    : undefined;

  useEffect(() => {
    if (slackError === undefined) return;
    Sentry.captureException(slackQuery.error, {
      tags: { error_type: 'slack_settings_fetch_personal' },
    });
  }, [slackQuery.error, slackError]);

  return (
    <>
      <SlackIntegrationCard
        settings={slackQuery.data}
        onConnect={controller.openConnect}
        onManage={controller.openConnect}
        onDisconnect={controller.openDisconnect}
        isLoading={slackQuery.isLoading}
        error={slackError}
      />
      {controller.connectOpen && (
        <ConnectSlackModal
          key={PERSONAL_SLACK_SCOPE_ID}
          open={controller.connectOpen}
          onOpenChange={controller.setConnectOpen}
          scopeId={PERSONAL_SLACK_SCOPE_ID}
          settings={slackQuery.data}
          isLoading={slackQuery.isLoading}
          onSave={controller.handleSave}
          onRequestDisconnect={controller.handleRequestDisconnect}
        />
      )}
      {controller.disconnectOpen && (
        <DisconnectSlackDialog
          isOpen={controller.disconnectOpen}
          onClose={controller.closeDisconnect}
          onConfirm={controller.handleConfirmDisconnect}
          isLoading={deleteMutation.isPending}
        />
      )}
    </>
  );
}

PersonalSlackIntegration.displayName = 'PersonalSlackIntegration';
